/**
 * @file PublicationsUploadForm.js
 * @module src/js/components/PublicationsUploadForm
 * @description
 * **A specialized upload form component for publications.**
 * - Allows users to upload publication entries with date, title, and type.
 * - Validates input fields and enables upload button accordingly.
 * - Supports both upload (POST) and update (PUT) operations.
 * - Redirects to login if user is 'Guest'.
 * 
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useUpload hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import useUpload from "../hooks/useUpload";
import useUpdatePublications from "../hooks/useUpdatePublications";
import SpanARow from "./SpanARow";
import DateSelection from "./DateSelection";
import CanUploadCheck from "./CanUploadCheck";

export default function PublicationsUploadForm({
    item,
    categories,
    setData,
    setCurrentCategory,
    setUpdatedID,
}) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { uid } = useUser();
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [year, setYear] = useState('');
    const [month, setMonth] = useState('');
    const [title, setTitle] = useState('');
    const [type, setType] = useState('choose');
    const [canUpload, setCanUpload] = useState(false);
    const [uploading, setUploading] = useState(false);
    const updatePublications = useUpdatePublications({
        setData,
        setUpdating: setUploading,
        setUpdatedID, // may be undefined (not needed when uploading)
        setShowUploadForm,
    });
    const upload = useUpload({
        setData,
        setUploading,
        setShowUploadForm,
    });

    if (item != null && typeof setUpdatedID !== "function") {
        throw new Error("setUpdatedID must be provided when item is given.");
    }

    const body = useMemo(() => ({
        y: year,
        m: month,
        title,
        type,
        id: item ? item.PublicationsID : undefined
    }), [year, month, title, type, item]);

    useEffect(() => {
        const today = new Date();
        if (item) {
            const [yearPart, monthPart] = item.Date.split('-');
            setYear(yearPart);
            setMonth(monthPart);
        } else {
            setYear(String(today.getFullYear())); // Get year (YYYY)
            setMonth(String(today.getMonth() + 1).padStart(2, "0")); // Get month (MM)
        }
        setTitle(item?.Title || '');
        setType(item?.Type || 'choose');
        if (item) {
            setShowUploadForm(true); // For PUT (update)
        }
    }, [item, showUploadForm]);

    return uid === 'Guest' ? (
        <SpanARow
            colSpan="3"
            onClick={() => navigate(
                '/login',
                { state: { from: pathname } }
            )}
            text='🔑 Log in to upload, edit, or delete'
        />
    ) : (
        showUploadForm ? (
            <tr>
                {/* Date */}
                <DateSelection
                    year={year}
                    month={month}
                    setYear={setYear}
                    setMonth={setMonth}
                    monthOptional={true}
                />

                {/* Title */}
                <td>
                    <textarea
                        type="text" placeholder="Title (Markdown supported. E.g., **bold**, *italic*, [link](url))"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required style={{ height: '10em', fontSize: '0.8em', marginBottom: '5px' }} autoFocus
                    />
                    <select
                        onChange={(e) => setType(e.target.value)}
                        value={type}
                        style={{ marginTop: '5px', marginBottom: '5px' }}
                    >
                        <option value='choose' disabled>-- Please choose a category --</option>
                        {Object.entries(categories).map(([key, value], i) => (
                            <option key={i} value={key}>{value}</option>
                        ))}
                    </select>
                    <CanUploadCheck
                        hasDateField={true}
                        title={title}
                        type={type}
                        setCanUpload={setCanUpload}
                    />
                </td>

                {/* Upload and Cancel Buttons */}
                <td className='hori-vert-center' width='10%'>
                    <button
                        type="submit"
                        style={{ marginBottom: '15px' }}
                        disabled={!canUpload || uploading || (
                            year === item?.Date.split('-')[0] &&
                            month === item?.Date.split('-')[1] &&
                            title === item?.Title &&
                            type === item?.Type
                        )}
                        onClick={async () => {
                            if (item) {
                                await updatePublications({ body });
                            } else {
                                await upload({
                                    request: `/api/publications/${type}`,
                                    body,
                                    from: pathname,
                                });
                            }
                            setCurrentCategory(categories[type]);
                        }}
                    >
                        {item ? 'Save' : 'Upload'}
                    </button>
                    <button
                        onClick={() => {
                            if (item) {
                                setUpdatedID(-1); // For PUT (update)
                            } else {
                                setShowUploadForm(false); // For POST (upload)
                            }
                        }}
                        disabled={uploading}
                    >
                        Cancel
                    </button>
                </td>
            </tr>
        ) : (
            <SpanARow
                colSpan="3"
                onClick={() => setShowUploadForm(true)}
                text="📚 Upload a publication"
            />
        )
    );
}